import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { drawSparkline, clearSparkline } from '../../shell/symbolGrid/SparklineRenderer';
import type { ChartThemeName } from '../../shell/symbolGrid/chartUtils';
import { registerChartFrame } from './chart-clock';
import type { RingBuffer } from './ring-buffer';

/*
 * <vitesse-sparkline> — the inline, collapsed-row sparkline. A thin Lit wrapper
 * over the shipped Canvas2D `drawSparkline` (the symbol grid's renderer — reused,
 * never reinvented), reading a RingBuffer SHARED BY REFERENCE with the section.
 *
 * It owns no data: the section push()es into the ring, this element redraws from
 * it on the shared chart clock. Passing `ring.version` lets drawSparkline reuse
 * its per-canvas min/max cache between samples. Light DOM; the host is sized by
 * the section SCSS (the canvas fills it), so there are no inline styles.
 */
@customElement('vitesse-sparkline')
export class VitesseSparkline extends LitElement {
  @property({ attribute: false }) ring?: RingBuffer;
  @property({ type: Number }) timeWindowMs = 10_000;
  @property({ type: Number }) sampleRateHz = 10;
  @property() theme: ChartThemeName = 'frost';

  private unClock?: () => void;
  private canvas: HTMLCanvasElement | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.unClock = registerChartFrame(() => this.draw());
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unClock?.();
    this.canvas = null;
  }

  private draw(): void {
    const c = (this.canvas ??= this.querySelector('canvas'));
    if (!c || !this.ring) return;
    if (this.ring.count === 0) {
      clearSparkline(c);
      return;
    }
    drawSparkline(
      c,
      this.ring.timestamps,
      this.ring.values,
      this.ring.count,
      { timeWindowMs: this.timeWindowMs, sampleRateHz: this.sampleRateHz, theme: this.theme },
      this.ring.head,
      this.ring.capacity,
      this.ring.version,
      true, // holdLast — a steady variable flatlines, never goes blank
    );
  }

  render() {
    return html`<canvas class="live-chart-canvas"></canvas>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-sparkline': VitesseSparkline;
  }
}
